import {
	CheckCircle,
	UploadSimpleIcon,
	WarningCircle,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUploadMqttSources } from "@/api/reseller-manager/mutations";
import type { UploadMqttSourcesResponse } from "@/api/reseller-manager/types";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

type Step = "dropzone" | "result";

interface CsvUploadDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function CsvUploadDialog({ open, onOpenChange }: CsvUploadDialogProps) {
	const { t } = useTranslation();
	const [step, setStep] = useState<Step>("dropzone");
	const [response, setResponse] = useState<UploadMqttSourcesResponse | null>(
		null,
	);
	const upload = useUploadMqttSources();

	useEffect(() => {
		if (open) {
			setStep("dropzone");
			setResponse(null);
		}
	}, [open]);

	function handleFile(file: File) {
		upload.mutate(
			{ file },
			{
				onSuccess: (data) => {
					setResponse(data);
					setStep("result");
				},
				onError: () => {
					toast.error(t("csvUpload.error"));
				},
			},
		);
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (upload.isPending) return;
				onOpenChange(o);
			}}
		>
			<DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0">
				<DialogHeader className="p-4 pb-2">
					<DialogTitle>{t("csvUpload.title")}</DialogTitle>
					<DialogDescription>{t(`csvUpload.desc.${step}`)}</DialogDescription>
				</DialogHeader>
				{step === "dropzone" && (
					<CsvUploadDropzone onFile={handleFile} isPending={upload.isPending} />
				)}
				{step === "result" && response && (
					<CsvUploadResult
						response={response}
						onClose={() => onOpenChange(false)}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}

interface CsvUploadDropzoneProps {
	onFile: (file: File) => void;
	isPending: boolean;
}

function CsvUploadDropzone({ onFile, isPending }: CsvUploadDropzoneProps) {
	const { t } = useTranslation();
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);
			if (isPending) return;
			const dropped = e.dataTransfer.files[0];
			if (dropped) onFile(dropped);
		},
		[onFile, isPending],
	);

	const handleDragOver = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			if (isPending) return;
			setIsDragging(true);
		},
		[isPending],
	);

	const handleDragLeave = useCallback(() => setIsDragging(false), []);

	return (
		<div className="flex flex-col gap-4 px-4 pb-4">
			<div className="flex items-center justify-between">
				<Label></Label>
				<a
					href="/CSV_EXAMPLE_MQTT_SOURCES_IMPORT.csv"
					download
					className="text-xs text-muted-foreground underline hover:text-foreground"
				>
					{t("csvUpload.downloadTemplate")}
				</a>
			</div>
			<button
				type="button"
				disabled={isPending}
				onClick={() => inputRef.current?.click()}
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				className={`flex flex-col items-center gap-2 rounded-md border-2 border-dashed px-4 py-10 text-center text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
					isDragging
						? "border-primary bg-primary/5 text-primary"
						: "border-muted-foreground/25 text-muted-foreground hover:border-primary/50 hover:text-foreground"
				}`}
			>
				<UploadSimpleIcon aria-hidden="true" className="size-6" />
				<span>
					{isPending
						? t("csvUpload.importing")
						: isDragging
							? t("csvUpload.dragActive")
							: t("csvUpload.dragDrop")}
				</span>
			</button>
			<input
				ref={inputRef}
				type="file"
				accept=".csv,text/csv"
				className="hidden"
				onChange={(e) => {
					const f = e.target.files?.[0];
					if (f) onFile(f);
					e.target.value = "";
				}}
			/>
		</div>
	);
}

interface CsvUploadResultProps {
	response: UploadMqttSourcesResponse;
	onClose: () => void;
}

function CsvUploadResult({ response, onClose }: CsvUploadResultProps) {
	const { t } = useTranslation();
	const skipped = response.skipped ?? [];

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4">
			{response.detail && (
				<div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
					<CheckCircle
						aria-hidden="true"
						className="mt-0.5 size-4 shrink-0 text-primary"
					/>
					<span>{response.detail}</span>
				</div>
			)}

			{skipped.length > 0 && (
				<>
					<div className="flex items-center gap-2 text-sm">
						<WarningCircle
							aria-hidden="true"
							className="size-4 text-amber-600"
						/>
						<span className="font-medium">
							{t("csvUpload.skippedTitle", { count: skipped.length })}
						</span>
					</div>
					<div className="min-h-0 flex-1 overflow-auto rounded-md border">
						<table className="w-full text-sm">
							<thead className="sticky top-0 z-10 bg-muted/50">
								<tr className="border-b text-left">
									<th className="w-20 px-3 py-2 font-medium">
										{t("csvUpload.skippedRow")}
									</th>
									<th className="px-3 py-2 font-medium">
										{t("csvUpload.skippedReason")}
									</th>
								</tr>
							</thead>
							<tbody>
								{skipped.map((s) => (
									<tr
										key={`${s.row}-${s.reason}`}
										className="border-b last:border-b-0"
									>
										<td className="px-3 py-2 tabular-nums">{s.row}</td>
										<td className="px-3 py-2 text-muted-foreground">
											{s.reason}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}

			<div className="flex justify-end">
				<Button onClick={onClose}>{t("csvUpload.close")}</Button>
			</div>
		</div>
	);
}
